import {
  AttachMoney,
  BarChart,
  Business as BusinessIcon,
  Category,
  Gavel,
  People,
  TrendingUp,
} from '@mui/icons-material';
import { Box, Grid, InputAdornment, TextField } from '@mui/material';
import { SummaryResponse } from 'lib/api/publicMarket';
import CombinedChart from 'lib/designSystem/Charts/CombinedChart';
import PieChart from 'lib/designSystem/Charts/PieChart';
import SemiPieChart from 'lib/designSystem/Charts/SemiPieChart';
import SimpleBarChart from 'lib/designSystem/Charts/SimpleBarChart';
import StatCard from 'lib/designSystem/Charts/StatCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { formatMoney } from 'lib/functions/utils';
import React, { useMemo, useState } from 'react';

export interface SummaryProps {
  loading: boolean;
  data: SummaryResponse | null;
}

const Summary: React.FC<SummaryProps> = ({ loading, data }) => {
  const [percentInput, setPercentInput] = useState<string>('4');

  const tenderTypeData = useMemo(() => {
    if (!data?.tenderByType) return [];
    return Object.entries(data.tenderByType).map(([type, count]) => ({
      id: type,
      label: type,
      value: count,
    }));
  }, [data?.tenderByType]);

  const tenderStatusData = useMemo(() => {
    if (!data?.tenderByStatus) return [];
    return Object.entries(data.tenderByStatus).map(([status, count]) => ({
      id: status,
      label: status,
      value: count,
    }));
  }, [data?.tenderByStatus]);

  const publishedTendersClosingSoonData = useMemo(() => {
    if (!data?.publishedTendersClosingSoon) return [];
    return [
      {
        id: 'today',
        label: 'Hoy',
        value: data.publishedTendersClosingSoon.today,
      },
      {
        id: 'in1Week',
        label: 'En 1 semana',
        value: data.publishedTendersClosingSoon.in1Week,
      },
      {
        id: 'in3Days',
        label: 'En 3 días',
        value: data.publishedTendersClosingSoon.in3Days,
      },
      {
        id: 'in1MonthOrMore',
        label: 'En 1 mes o más',
        value: data.publishedTendersClosingSoon.in1MonthOrMore,
      },
    ];
  }, [data?.publishedTendersClosingSoon]);

  const potentialIncomePercent = useMemo(() => {
    const parsed = parseFloat(percentInput);
    return Number.isNaN(parsed) ? 0 : parsed;
  }, [percentInput]);

  const potentialIncome = useMemo(() => {
    const baseAmount = data?.notAwardedTendersAmount ?? 0;
    return Math.round((baseAmount * potentialIncomePercent) / 100);
  }, [data?.notAwardedTendersAmount, potentialIncomePercent]);

  if (loading) return <CenteredCircularProgress />;

  return (
    <Box sx={{ py: 2 }}>
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            label="Monto Transado (CLP)"
            value={formatMoney(data?.amountTraded ?? 0)}
            valueLabel="CLP"
            color="#0F23DF"
            icon={<AttachMoney sx={{ fontSize: 24 }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <StatCard
            label="Licitaciones"
            value={data?.tenderQuantity || 0}
            valueLabel=""
            color="#10B981"
            icon={<Gavel sx={{ fontSize: 24 }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Proveedores"
            value={data?.suppliers?.length || 0}
            valueLabel=""
            color="#F59E0B"
            icon={<BusinessIcon sx={{ fontSize: 24 }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Compradores"
            value={data?.buyers?.length || 0}
            valueLabel=""
            color="#0F23DF"
            icon={<People sx={{ fontSize: 24 }} />}
          />
        </Grid>
      </Grid>

      <Grid container sx={{ mb: 1.5 }}>
        <Grid item xs={12} md={4}>
          <PieChart
            data={tenderTypeData}
            title="Total Licitaciones por Estado"
            icon={<BarChart sx={{ fontSize: 18, color: '#10B981' }} />}
            color="#10B981"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <PieChart
            data={tenderStatusData}
            title="Total Licitaciones por Estado"
            icon={<BarChart sx={{ fontSize: 18, color: '#F59E0B' }} />}
            color="#F59E0B"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SemiPieChart
            data={publishedTendersClosingSoonData}
            title="Licitaciones por Próximo Cierre"
            icon={<BarChart sx={{ fontSize: 18, color: '#8B5CF6' }} />}
            color="#8B5CF6"
          />
        </Grid>
      </Grid>

      <Grid container sx={{ mb: 1 }}>
        <Grid item xs={8}>
          <CombinedChart
            data={data?.awardedTenders || {}}
            title="Licitaciones adjudicadas"
            icon={<TrendingUp sx={{ fontSize: 18, color: '#0F23DF' }} />}
            primaryColor="#0F23DF"
            secondaryColor="#D62839"
            xAxisLabel="Fecha de adjudicación"
            yAxisLabel="Cantidad"
            rightYAxisLabel="Monto (CLP)"
            sortBy="desc"
            customMargin={{ left: 40, right: 40, top: 10, bottom: 40 }}
            customAmountValueFormatter={(value) => formatMoney(value || 0)}
          />
        </Grid>
        <Grid
          item
          xs={4}
          sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
        >
          <StatCard
            label="Cierre en menos de 30 días"
            value={formatMoney(data?.closingSoonTendersAmount || 0)}
            valueLabel="CLP"
            color="#8B5CF6"
            icon={<AttachMoney sx={{ fontSize: 18, color: '#8B5CF6' }} />}
          />
          <StatCard
            label="No adjudicadas"
            value={formatMoney(data?.notAwardedTendersAmount || 0)}
            valueLabel="CLP"
            color="#0F23DF"
            icon={<AttachMoney sx={{ fontSize: 18, color: '#0F23DF' }} />}
          />
          <Box sx={{ position: 'relative' }}>
            <Box sx={{ position: 'absolute', top: 12, right: 20, zIndex: 1 }}>
              <TextField
                size="small"
                type="number"
                inputMode="decimal"
                value={percentInput}
                onChange={(e) => setPercentInput(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                  inputProps: { min: 0, max: 100, step: 0.1 },
                }}
                sx={{
                  width: 72,
                  '& .MuiInputBase-input': {
                    textAlign: 'right',
                    padding: '4px 6px',
                  },
                }}
              />
            </Box>
            <StatCard
              label="Ingreso potencial"
              value={formatMoney(potentialIncome)}
              valueLabel="CLP"
              color="#2ECC71"
              icon={<AttachMoney sx={{ fontSize: 18, color: '#2ECC71' }} />}
            />
          </Box>
        </Grid>
      </Grid>

      <Grid container sx={{ mb: 1.5 }}>
        <Grid item xs={4}>
          <SimpleBarChart
            data={data?.top10BuyersByAwardedAmount || {}}
            title="Top 10 mandantes"
            color="#0F23DF"
            icon={<People sx={{ fontSize: 18, color: '#0F23DF' }} />}
            direction="horizontal"
            maxItems={10}
            sortBy="desc"
            xAxisLabel="Monto (CLP)"
            customMargin={{ left: 80, right: 0, top: 0, bottom: 20 }}
            valueFormatter={(value) => formatMoney(value || 0)}
          />
        </Grid>
        <Grid item xs={4}>
          <SimpleBarChart
            data={data?.top10SuppliersByAwardedAmount || {}}
            title="Top 10 proveedores"
            color="#F59E0B"
            icon={<BusinessIcon sx={{ fontSize: 18, color: '#F59E0B' }} />}
            direction="horizontal"
            maxItems={10}
            sortBy="desc"
            xAxisLabel="Monto (CLP)"
            customMargin={{ left: 80, right: 0, top: 0, bottom: 20 }}
            valueFormatter={(value) => formatMoney(value || 0)}
          />
        </Grid>
        <Grid item xs={4}>
          <SimpleBarChart
            data={data?.top10CategoriesByAwardedAmount || {}}
            title="Top 10 categories"
            color="#2ECC71"
            icon={<Category sx={{ fontSize: 18, color: '#2ECC71' }} />}
            direction="horizontal"
            maxItems={10}
            sortBy="desc"
            xAxisLabel="Monto (CLP)"
            customMargin={{ left: 75, right: 0, top: 0, bottom: 20 }}
            valueFormatter={(value) => formatMoney(value || 0)}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Summary;
